import { useState } from 'react';

import { appColors, AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  OfficeHoursMessage,
  SearchedUser,
  SENDER_TYPE,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  setIsLoading,
  setOfficeHoursMessages,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { setCoachingUserWithDocuments } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

import {
  Avatar,
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';

const SUPERCOACH_DROPDOWN_STRINGS = appStrings.modules.officeHours.supercoachDropdown;

export const SupercoachUserDropdown = () => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);

  const { coachingUser } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );

  const userId = useAppSelector(selectUserId);

  const [users, setUsers] = useState<SearchedUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const loadUsers = async () => {
    try {
      const response = await axios.get(`/api/get-supercoach-assignees`, {
        params: {
          supercoach_id: userId,
          full_last_name: true,
        },
      });

      setUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    }
  };

  const handleChange = async (event: SelectChangeEvent) => {
    const selectedUser = users.find((user) => `${user.id}` === event.target.value);

    if (!activeSession) {
      return;
    }

    if (coachingUser && !window.confirm(appStrings.modules.supercoach.confirmSwitch)) {
      return;
    }

    dispatch(
      setCoachingUserWithDocuments({ user: selectedUser || undefined, sessionType: activeSession }),
    );

    const selectedUserMessage: OfficeHoursMessage | undefined = selectedUser
      ? {
          componentType: { type: 'SELECTED_USER', user: selectedUser },
          sender: SENDER_TYPE.USER,
        }
      : undefined;

    const updatedMessages: OfficeHoursMessage[] = [
      { text: appStrings.modules.supercoach.welcome, sender: SENDER_TYPE.AI },
      ...(selectedUserMessage ? [selectedUserMessage] : []),
    ];

    dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(updatedMessages));
    dispatch(createSessionAction(setIsLoading, activeSession)(true));

    // set the actual messages after a delay
    setTimeout(() => {
      dispatch(
        createSessionAction(
          setOfficeHoursMessages,
          activeSession,
        )([
          ...updatedMessages,
          { text: appStrings.modules.supercoach.selectPrompt, sender: SENDER_TYPE.AI },
        ]),
      );
      dispatch(createSessionAction(setIsLoading, activeSession)(false));
    }, 750);
  };

  if (!activeSession) {
    return null;
  }

  return (
    <Select
      key={coachingUser?.id ?? null}
      value={`${coachingUser?.id}`}
      onChange={handleChange}
      onOpen={loadUsers}
      displayEmpty
      renderValue={() => (
        <Box sx={renderValueContainer}>
          <Typography sx={menuTitle}>{SUPERCOACH_DROPDOWN_STRINGS.selectStudent}</Typography>
          <AppIcon iconName="ChevronDown" diameter="16px" color="Colors/Text/text-primary" />
        </Box>
      )}
      sx={menuStyles}
    >
      <Box sx={{ p: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={SUPERCOACH_DROPDOWN_STRINGS.searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={textFieldStyles}
        />
      </Box>
      {filteredUsers.map((user) => (
        <MenuItem key={user.id} value={user.id.toString()} sx={menuItemStyles}>
          <Avatar src={user.profile_image} alt={user.name} sx={avatarStyles} />
          <Typography fontWeight={500}>{user.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

const menuStyles: SxProps<Theme> = {
  height: '28px',
  width: 'fit-content',
  padding: '0px 10px',
};

const renderValueContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  gap: '4px',
};

const menuTitle: React.CSSProperties = {
  fontSize: '10.5pt',
  fontWeight: '500',
};

const menuItemStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
} as const;

const avatarStyles: SxProps<Theme> = {
  width: 24,
  height: 24,
} as const;

const textFieldStyles: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    padding: 0,
  },
  '& .MuiOutlinedInput-root': {
    minHeight: '32px',
    padding: 0,
    '& fieldset': {
      borderColor: appColors.color.blue4,
    },
    '& input': {
      padding: '0 14px',
    },
  },
} as const;
